import { createSelector } from "@reduxjs/toolkit";

import { Integration } from "../../model/integration/Integration";
import { IntegrationKind } from "../../model/integration/types/IntegrationKind";
import { IntegrationConfigFacebook } from "../../model/integration/types/config/IntegrationConfigFacebook";
import { IntegrationConfigFacebookMeta } from "../../model/integration/types/config/IntegrationConfigFacebookMeta";
import { IntegrationConfigGoogleDrive } from "../../model/integration/types/config/IntegrationConfigGoogleDrive";
import { IntegrationConfigWhatsappMeta } from "../../model/integration/types/config/IntegrationConfigWhatsappMeta";
import { RootState } from "../store";
import { IntegrationConfigWhatsappDirectChat } from "../../model/integration/types/config/IntegrationConfigWhatsappDirectChat";
import { ConversationComment } from "../../model/integration/dto/meta/MetaConversationComment";
import { chatNeedsAttention } from "../../routes/integration/utils/integration-utils";

const state = (state: RootState) => state.integration;

const selectUserIntegrations = createSelector(
  state,
  (state) => state.integrations || [],
);

const selectWhatsAppIntegration = createSelector(
  state,
  (state) => state.whatsAppIntegration,
);

const selectFBIntegration = createSelector(
  state,
  (state) =>
    state.integrations?.find(
      (i) => i.kind === IntegrationKind.FACEBOOK,
    ) as null | Integration<IntegrationConfigFacebook>,
);

const selectFBResolveFieldsData = createSelector(
  state,
  (state) => state.integrationFBResolveFieldsData,
);

const selectFacebookMetaIntegration = createSelector(
  selectUserIntegrations,
  (integrations) =>
    integrations.find(
      (integration) => integration.kind === IntegrationKind.FACEBOOK_META,
    ) as null | Integration<IntegrationConfigFacebookMeta>,
);

const selectWhatsAppMetaIntegration = createSelector(
  selectUserIntegrations,
  (integrations) =>
    integrations.find(
      (integration) => integration.kind === IntegrationKind.WHATSAPP_META,
    ) as null | Integration<IntegrationConfigWhatsappMeta>,
);

const selectWhatsAppDirectIntegration = createSelector(
  selectUserIntegrations,
  (integrations) =>
    integrations.find(
      (integration) => integration.kind === IntegrationKind.WHATSAPP_DIRECT,
    ) as null | Integration<IntegrationConfigWhatsappDirectChat>,
);

const selectWhatsAppDirectChatsList = createSelector(
  state,
  (state) => state.whatsappIntegrationDirectChatsList,
);

const selectWhatsAppDirectChatsListLoading = createSelector(
  state,
  (state) => state.whatsappIntegrationDirectChatsListLoading,
);

const selectWhatsAppDirectChatMessagesLoading = createSelector(
  state,
  (state) => state.chatMessages?.loading || false,
);

const selectWhatsAppDirectChatMessagesById = createSelector(
  state,
  (state: RootState, id: string) => id,
  (state, id) => state.chatMessages?.entities?.[id] || {},
);

const selectWhatsAppDirectChatMetaById = createSelector(
  state,
  (state: RootState, id: string) => id,
  (state, id) => state.whatsappIntegrationDirectChatsListMeta?.[id] || {},
);

//

const selectWhatsAppMetaChatsList = createSelector(
  state,
  (state) => state.whatsappIntegrationMetaChatsList,
);

const selectWhatsAppMetaChatsElsewhere = createSelector(
  state,
  (state) => state.whatsappIntegrationMetaChatsElsewhere,
);

const selectTotalUnreadWhatsAppMetaChats = createSelector(
  selectWhatsAppMetaChatsList,
  selectWhatsAppMetaChatsElsewhere,
  (chats, elsewhere) =>
    (chats || []).reduce((total, chat) => total + (chat.unreadCount || 0), 0) +
    (elsewhere?.unread || 0),
);

const selectWhatsAppMetaChatsAttentionCount = createSelector(
  selectWhatsAppMetaChatsList,
  selectWhatsAppMetaChatsElsewhere,
  (chats, elsewhere) =>
    (chats || []).filter(chatNeedsAttention).length +
    (elsewhere?.needsAttention || 0),
);

const selectWhatsAppMetaChatsListLoaded = createSelector(
  state,
  (state) => state.whatsappIntegrationMetaChatsListLoaded,
);

const selectWhatsAppMetaChatsListHasMore = createSelector(state, (state) =>
  Boolean(state.whatsappIntegrationMetaChatsListNextCursor),
);

const selectWhatsAppMetaChatsListNextCursor = createSelector(
  state,
  (state) => state.whatsappIntegrationMetaChatsListNextCursor,
);

const selectWhatsAppMetaChatsListReachedAt = createSelector(
  state,
  (state) => state.whatsappIntegrationMetaChatsListReachedAt,
);

const selectWhatsAppMetaChatsListLoadingMore = createSelector(
  state,
  (state) => state.whatsappIntegrationMetaChatsListLoadingMore,
);

const selectWhatsAppMetaChatsView = createSelector(
  state,
  (state) => state.whatsappIntegrationMetaChatsView,
);

const selectWhatsAppMetaChatsListLoading = createSelector(
  state,
  (state) => state.whatsappIntegrationMetaChatsListLoading,
);

const selectWhatsAppMetaChatById = createSelector(
  state,
  (state: RootState, id: string) => id,
  (state, id) =>
    state.whatsappIntegrationMetaChatsList?.find((chat) => chat._id === id),
);

const selectWhatsAppMetaNumberHealth = createSelector(
  state,
  (state) => state.whatsappIntegrationMetaNumberHealth,
);

const selectWhatsAppMetaMediaStorage = createSelector(
  state,
  (state) => state.whatsappIntegrationMetaMediaStorage,
);

const selectWhatsAppMetaCampaigns = createSelector(
  state,
  (state) => state.whatsappIntegrationMetaCampaigns || [],
);

const selectWhatsAppMetaCampaignsLoaded = createSelector(
  state,
  (state) => state.whatsappIntegrationMetaCampaignsLoaded,
);

const selectWhatsAppMetaTemplates = createSelector(
  state,
  (state) => state.whatsappIntegrationMetaTemplates || [],
);

const selectWhatsAppMetaMessagesPaging = createSelector(
  state,
  (state: RootState, conversationId: string) => conversationId,
  (state, conversationId) =>
    state.whatsappIntegrationMetaMessagesPaging?.[conversationId],
);

const selectWhatsAppMetaComments = createSelector(
  state,
  (state: RootState, conversationId: string) => conversationId,
  (state, conversationId): ConversationComment[] | undefined =>
    state.whatsappIntegrationMetaComments?.[conversationId],
);

const selectWhatsAppMetaOpenCommentsCount = createSelector(
  selectWhatsAppMetaComments,
  (comments) => (comments || []).filter((c) => !c.resolvedAt).length,
);

//

const selectGoogleDriveIntegration = createSelector(
  selectUserIntegrations,
  (integrations) =>
    integrations.find(
      (integration) => integration.kind === IntegrationKind.GOOGLE_DRIVE,
    ) as null | Integration<IntegrationConfigGoogleDrive>,
);

const selectGoogleDriveFiles = createSelector(
  state,
  (state) => state.googleDriveFiles || [],
);

const selectGoogleDriveFilesLoading = createSelector(
  state,
  (state) => state.googleDriveFilesLoading,
);

const selectGoogleDriveFileActionLoading = createSelector(
  state,
  (state) => state.googleDriveFileActionLoading,
);

export const integrationSliceSelectors = {
  selectWhatsAppMetaComments,
  selectWhatsAppMetaOpenCommentsCount,
  selectWhatsAppMetaMessagesPaging,
  selectUserIntegrations,
  selectWhatsAppIntegration,
  selectFBResolveFieldsData,
  selectFBIntegration,
  selectFacebookMetaIntegration,
  selectWhatsAppMetaIntegration,
  selectWhatsAppDirectIntegration,
  selectWhatsAppDirectChatsList,
  selectWhatsAppDirectChatsListLoading,
  selectWhatsAppDirectChatMessagesLoading,
  selectWhatsAppDirectChatMessagesById,
  selectWhatsAppDirectChatMetaById,
  selectWhatsAppMetaChatsList,
  selectWhatsAppMetaChatsListLoading,
  selectWhatsAppMetaChatById,
  selectWhatsAppMetaTemplates,
  selectWhatsAppMetaNumberHealth,
  selectWhatsAppMetaMediaStorage,
  selectWhatsAppMetaCampaigns,
  selectWhatsAppMetaCampaignsLoaded,
  selectTotalUnreadWhatsAppMetaChats,
  selectGoogleDriveIntegration,
  selectGoogleDriveFiles,
  selectGoogleDriveFilesLoading,
  selectGoogleDriveFileActionLoading,
  selectWhatsAppMetaChatsAttentionCount,
  selectWhatsAppMetaChatsListLoaded,
  selectWhatsAppMetaChatsListHasMore,
  selectWhatsAppMetaChatsListNextCursor,
  selectWhatsAppMetaChatsListLoadingMore,
  selectWhatsAppMetaChatsListReachedAt,
  selectWhatsAppMetaChatsView,
};
